/* eslint-disable react-hooks/rules-of-hooks */
import { SearchAgentDrawer, SearchAgentFormData } from '@components/SearchAgentDrawer';
import { HeadCell, Menu, MenuAction, Table, TextCell, TextFallback, TimestampCell, useMenu } from '@components/ui';
import { LeadToggleCell } from '@components/ui/LeadToggleCell';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  SvgIcon,
  Tooltip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import LinkIcon from '@material-ui/icons/Link';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import StarIcon from '@material-ui/icons/Star';
import { navigate } from '@reach/router';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { merge } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PurchaseAgentSvg } from 'src/assets/icons/purchase-agent.svg';
import { QueryKeys, RoutePaths } from 'src/constants';
import { usePurchaseAgents } from 'src/hooks/usePurchaseAgents';
import { useSearchAgentDelete } from 'src/hooks/useSearchAgentDelete';
import { useSearchAgentPreview } from 'src/hooks/useSearchAgentPreview';
import { useSearchAgents } from 'src/hooks/useSearchAgents';
import { useSearchAgentUpdate } from 'src/hooks/useSearchAgentUpdate';
import { useSources } from 'src/hooks/useSources';
import { useUser } from 'src/hooks/useUser';
import { NotificationChannel, PurchaseAgentListItem, SearchAgentDTO } from 'src/modules/generated/api';
import { isSystemAgent } from 'src/modules/system-agents';

import { getDefaultFilter, persistFilter, transformForForm } from './cars-table-next/filter/lib';

const PurchaseAgentIcon = () => {
  const theme = useTheme();
  return (
    <SvgIcon
      component={PurchaseAgentSvg as any}
      viewBox="0 0 32 32"
      fontSize="small"
      style={{ color: theme.palette.intelligence.main }}
    />
  );
};

type SearchAgent = {
  id: string;
  searchAgentDisplayName: string;
  notification: NotificationChannel;
  lastUpdatedAt?: string;
  active?: boolean;
  favorite?: boolean;
  preset: SearchAgentDTO;
};

export type SearchAgentTableProps = {
  className?: string;
  onCreatePurchaseAgent?: (searchAgentId: string) => void;
};

enum SearchAgentAction {
  edit,
  load,
  delete,
}

const columns: ColumnDef<SearchAgent>[] = [
  {
    id: 'name',
    header: () => <HeadCell text="common.name" />,
    cell: ({ row }) => {
      const { t } = useTranslation();
      const queryClient = useQueryClient();
      const { id, active, searchAgentDisplayName, favorite } = row.original ?? {};
      const { mutate: updateSearchAgent } = useSearchAgentUpdate();
      const [inView, setInView] = useState(false);
      const cellRef = useRef<HTMLSpanElement>(null);
      const {
        data: preview,
        isLoading: previewIsLoading,
        isFetching: previewIsFetching,
      } = useSearchAgentPreview(id, inView);
      const [copySuccess, setCopySuccess] = useState<string>('');

      // Lazy-load the preview count: only fire the API call once the row enters the viewport.
      // Prevents N simultaneous listCars requests on page load for off-screen rows.
      useEffect(() => {
        const el = cellRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setInView(true);
              observer.disconnect();
            }
          },
          { threshold: 0.1 },
        );
        observer.observe(el);
        return () => observer.disconnect();
      }, []);

      const handleChangeActive = () => {
        const searchAgents = queryClient.getQueryData<SearchAgentDTO[]>([QueryKeys.searchAgents]);
        const target = searchAgents?.find((searchAgent) => searchAgent.id === id);

        if (target) {
          updateSearchAgent({ id: id!, ...target, active: !active });
        }
      };

      // if copySuccess is true, set it back to false after 5 seconds
      // eslint-disable-next-line consistent-return
      useEffect(() => {
        if (copySuccess) {
          const timer = setTimeout(() => setCopySuccess(''), 3000);
          return () => clearTimeout(timer);
        }
      }, [copySuccess]);

      return (
        <span ref={cellRef}>
          <LeadToggleCell
            text={
              <>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <Grid container direction="row" alignItems="center" wrap="nowrap">
                  <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                    {favorite ? (
                      <Tooltip title={t('searchAgentTable.pinned')} placement="top" arrow>
                        <StarIcon fontSize="small" style={{ marginInlineEnd: 4 }} />
                      </Tooltip>
                    ) : null}
                  </Grid>
                  <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                    {searchAgentDisplayName}
                  </Grid>
                  <Grid item style={{ display: 'flex', alignItems: 'center', paddingLeft: '4px' }}>
                    <Tooltip title={t('searchAgentTable.link')} placement="top" arrow>
                      <LinkIcon
                        fontSize="small"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `${window.location.origin}/cars?searchagent=${row.original.id}`,
                          );
                          setCopySuccess(t('searchAgentTable.copied'));
                        }}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                    {copySuccess && (
                      <span style={{ marginLeft: '4px', fontSize: 'small', color: 'green' }}>{copySuccess}</span>
                    )}
                  </Grid>
                </Grid>
              </>
            }
            supportText={
              previewIsLoading ? (
                <TextFallback width="12ch" />
              ) : (
                <>
                  {t('searchAgentTable.preview', { count: preview?.recordsTotal })}
                  <Fade
                    in={previewIsFetching}
                    style={{
                      transitionDelay: previewIsFetching ? '300ms' : '800ms',
                    }}
                  >
                    <CircularProgress aria-label={t('common.isLoading')} size="12px" color="secondary" />
                  </Fade>
                </>
              )
            }
            checked={active ?? false}
            onChange={handleChangeActive}
          />
        </span>
      );
    },
  },
  {
    accessorKey: 'notification',
    header: () => <HeadCell text="common.notification" />,
    cell: (props) => {
      const { t } = useTranslation();
      const value = props.getValue();

      return <TextCell text={value && t(`notification.${value}` as any)} />;
    },
  },
  {
    accessorKey: 'lastUpdatedAt',
    header: () => <HeadCell text="common.lastUpdatedAt" />,
    cell: (props) => <TimestampCell timestamp={props.getValue<string | undefined>()} />,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row, table }) => {
      const { t } = useTranslation();
      const { mutate: deleteSearchAgent } = useSearchAgentDelete();
      const { isOpen: isMenuOpen, open: openMenu, close: closeMenu } = useMenu();
      const queryClient = useQueryClient();
      const [drawerOpen, setDrawerOpen] = useState(false);
      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
      const { id, preset } = row.original ?? {};
      const { mutate: updateSearchAgent, mutateAsync: updateSearchAgentAsync } = useSearchAgentUpdate();
      const { data: sources } = useSources();
      const { data: user } = useUser();
      const purchaseAgents = (table.options.meta as any)?.purchaseAgents as PurchaseAgentListItem[] | undefined;
      const linkedPurchaseAgents = useMemo(
        () => (purchaseAgents ?? []).filter((pa) => pa.linkedSearchAgent?.id?.value === id),
        [purchaseAgents, id],
      );

      const menuReducer = (action: MenuAction) => {
        switch (action.id) {
          case SearchAgentAction.delete: {
            if (id) {
              if (linkedPurchaseAgents.length > 0) {
                setConfirmDeleteOpen(true);
              } else {
                deleteSearchAgent(id);
              }
            }

            break;
          }
          case SearchAgentAction.load: {
            if (preset) {
              persistFilter(merge({}, getDefaultFilter({ sources, user }), transformForForm(preset)));
              navigate(RoutePaths.cars);
            } else {
              // TODO: emit error snackbar
              // eslint-disable-next-line no-console
              console.error('Unable to rehydrate SearchAgent.');
            }

            break;
          }
          case SearchAgentAction.edit: {
            setDrawerOpen(true);

            break;
          }

          // no default
        }
      };

      const handleCloseDrawer = () => {
        setDrawerOpen(false);
      };

      const handleSubmitDrawer = ({ searchAgentDisplayName, notifications, display }: SearchAgentFormData) => {
        const searchAgents = queryClient.getQueryData<SearchAgentDTO[]>([QueryKeys.searchAgents]);
        const target = searchAgents?.find((searchAgent) => searchAgent.id === id);
        const active = Object.values(notifications).some(Boolean);

        if (target) {
          updateSearchAgent({
            id: id!,
            ...target,
            searchAgentDisplayName,
            active,
            favorite: display.favorites,
          });
          setDrawerOpen(false);
        }
      };

      const onCreatePurchaseAgent = (table.options.meta as any)?.onCreatePurchaseAgent as
        ((searchAgentId: string) => void) | undefined;

      const handleSubmitAndCreatePA = onCreatePurchaseAgent
        ? async ({ searchAgentDisplayName, notifications, display }: SearchAgentFormData) => {
            const searchAgents = queryClient.getQueryData<SearchAgentDTO[]>([QueryKeys.searchAgents]);
            const target = searchAgents?.find((searchAgent) => searchAgent.id === id);
            const active = Object.values(notifications).some(Boolean);

            if (target) {
              await updateSearchAgentAsync({
                id: id!,
                ...target,
                searchAgentDisplayName,
                active,
                favorite: display.favorites,
              });
              setDrawerOpen(false);
              if (id) onCreatePurchaseAgent(id);
            } else {
              // eslint-disable-next-line no-console
              console.error('[SearchAgentTable] handleSubmitAndCreatePA: search agent not found in cache', id);
            }
          }
        : undefined;

      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
            {linkedPurchaseAgents.length > 0 && (
              <Tooltip
                title={t('searchAgentTable.linkedPurchaseAgents', {
                  names: linkedPurchaseAgents
                    .map((pa) => pa.name)
                    .filter(Boolean)
                    .join(', '),
                })}
                placement="top"
              >
                <Chip
                  label={linkedPurchaseAgents.length}
                  size="small"
                  style={{ borderRadius: 10, height: 20, minWidth: 20, fontSize: 11, cursor: 'default' }}
                />
              </Tooltip>
            )}
            {onCreatePurchaseAgent && (
              <Tooltip title={t('purchaseAgent.drawer.title')} placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (id) onCreatePurchaseAgent(id);
                  }}
                >
                  <PurchaseAgentIcon />
                </IconButton>
              </Tooltip>
            )}
            <Menu
              actions={[
                { id: SearchAgentAction.load, title: t('action.load'), icon: FlashOnIcon },
                { id: SearchAgentAction.edit, title: t('action.edit'), icon: EditIcon },
                { id: SearchAgentAction.delete, title: t('action.delete'), icon: DeleteIcon },
              ]}
              open={isMenuOpen}
              onAction={menuReducer}
              onOpen={openMenu}
              onClose={closeMenu}
            >
              {(triggerProps) => (
                <IconButton {...triggerProps} size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Menu>
          </div>

          <SearchAgentDrawer
            open={drawerOpen}
            onSubmit={handleSubmitDrawer}
            onSubmitAndCreatePurchaseAgent={handleSubmitAndCreatePA}
            onClose={handleCloseDrawer}
            searchAgent={row.original}
          />

          <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
            <DialogTitle>{t('searchAgentTable.deleteLinkedConfirmTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('searchAgentTable.deleteLinkedConfirmText')}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteOpen(false)} color="secondary" variant="outlined" size="small">
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  if (id) deleteSearchAgent(id);
                  setConfirmDeleteOpen(false);
                }}
                color="primary"
                variant="contained"
                size="small"
              >
                {t('common.delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      );
    },
  },
];

export const SearchAgentTable = ({ className, onCreatePurchaseAgent }: SearchAgentTableProps): JSX.Element => {
  const { data: searchAgents } = useSearchAgents();
  const { data: user } = useUser();
  const authProvider = user?.user?.authenticationProvider ?? '';
  const userId = user?.user?.id ?? '';
  const { data: purchaseAgents } = usePurchaseAgents({ enabled: !!onCreatePurchaseAgent });

  const data = useMemo(
    () =>
      searchAgents
        .filter((agent) => !isSystemAgent(agent.searchAgentDisplayName, authProvider, userId))
        .map<SearchAgent>((searchAgent, index) => ({
          id: searchAgent.id || String(index),
          lastUpdatedAt: searchAgent.lastModified,
          searchAgentDisplayName: searchAgent.searchAgentDisplayName ?? 'UNKNOWN',
          notification: searchAgent.active ? NotificationChannel.EMail : ('NONE' as NotificationChannel),
          active: searchAgent.active,
          favorite: searchAgent.favorite,
          preset: searchAgent,
        })),
    [authProvider, userId, searchAgents],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (searchAgent, index) => searchAgent.id ?? index,
    meta: { onCreatePurchaseAgent, purchaseAgents: onCreatePurchaseAgent ? purchaseAgents : undefined } as any,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <Table table={table as any} className={className} />;
};
